import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ReactCountryFlag from 'react-country-flag';
import { useSnackbar } from 'notistack';
import { useAppContext } from 'contexts/AppContext';
import countries from 'assets/jsons/countries';
import {
  checkIfFulfillRequired,
  formatPostalCode,
  matchExact,
  postalCodeIsValidRe,
  preventLosingCaretPosition,
  wait,
} from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsButton from 'ui-elements/buttons/FsButton';
import { FsFormGridWrapper } from 'ui-elements/form-elements/FsFormGridWrapper/FsFormGridWrapper';
import { FsFormFullWidthContainer } from 'ui-elements/form-elements/FsFormFullWidthContainer/FsFormFullWidthContainer';
import { FsFormGridItem } from 'ui-elements/form-elements/FsFormGridItem/FsFormGridItem';
import FsCheckboxWithLabel from 'ui-elements/form-elements/FsCheckboxWithLabel';

export interface BillingInfoFormProps {
  recipientName: string;
  address: string;
  city: string;
  postCode: string;
  country: string;
  vat: string;
  isVatUe: boolean;
}

interface FsBillingInfoFormProps extends Partial<BillingInfoFormProps> {
  onClose?: () => void;
  onChange?: (isValid: boolean, formData?: BillingInfoFormProps) => void;
  showButtons?: boolean;
}

const FsBillingInfoForm: React.FC<FsBillingInfoFormProps> = ({
  onClose,
  onChange,
  showButtons = true,
  recipientName: defaultRecipientName = '',
  address: defaultAddress = '',
  city: defaultCity = '',
  postCode: defaultPostCode = '',
  country: defaultCountry = 'United Kingdom',
  vat: defaultVat = '',
  isVatUe: defaultIsVatUe = false,
}) => {
  const {
    billingInfo: { update: updateStore },
  } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const formEl = useRef<HTMLFormElement>(null);
  const [recipientName, setRecipientName] = useState<string>(
    defaultRecipientName
  );
  const [address, setAddress] = useState<string>(defaultAddress);
  const [city, setCity] = useState<string>(defaultCity);
  const [postCode, setPostCode] = useState<string>(defaultPostCode);
  const [country, setCountry] = useState<string>(defaultCountry);
  const [vat, setVat] = useState<string>(defaultVat);
  const [isVatUe, setIsVatUe] = useState<boolean>(defaultIsVatUe);
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const fields = [
    {
      name: 'organization',
      value: recipientName,
      label: defaultLocales.invoiceRecipientName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRecipientName(e.target?.value);
      },
      required: true,
    },
    {
      name: 'street-address',
      value: address,
      label: defaultLocales.address,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setAddress(e.target?.value);
      },
      required: true,
    },
    {
      name: 'city',
      value: city,
      label: defaultLocales.city,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setCity(e.target?.value);
      },
      required: true,
    },
    {
      name: 'postal-code',
      value: postCode,
      label: defaultLocales.postCode,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        preventLosingCaretPosition(e);
        setPostCode(formatPostalCode(e.target?.value, postCode));
      },
      required: true,
    },
    {
      required: true,
      name: 'country',
      value: country,
      label: defaultLocales.country,
      options: countries.map(({ name, code }) => ({
        value: name,
        label: code,
      })),
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setVat(e.target?.value);
      },
    },
    {
      name: 'vat',
      value: vat,
      label: defaultLocales.vatNumber,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setVat(e.target?.value);
      },
      required: true,
    },
    {
      name: 'vat-ue',
      checked: isVatUe,
      label: defaultLocales.ueVatNumber,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIsVatUe(e.target?.checked);
      },
    },
  ];

  const [nameField, addressField, cityField, postCodeField] = fields;
  const countryField = fields.find(({ name }) => name === 'country');
  const defaultCountryFieldValue = countryField?.options?.find(
    ({ value }) => value === defaultCountry
  );
  const vatField = fields.find(({ name }) => name === 'vat');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const vatUeField = fields.find(({ name }) => name === 'vat-ue')!;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    await wait(1000);
    setPending(false);
    await updateStore();
    enqueueSnackbar(defaultLocales.billingInfoChangeNotificationSuccess, {
      variant: 'success',
    });
    if (onClose) onClose();
  };

  useEffect(() => {
    const isDefaultFormValid = checkIfFulfillRequired([
      recipientName,
      address,
      city,
      postCode,
      country,
      vat,
      isVatUe,
    ]);
    const isPostalCodeValid = matchExact(postalCodeIsValidRe, postCode);
    const isVatValid = vat.length >= 3;
    const isRecipientNameValid = recipientName.length >= 3;
    setIsFormValid(
      isDefaultFormValid &&
        isPostalCodeValid &&
        isVatValid &&
        isRecipientNameValid
    );

    if (onChange)
      onChange(isFormValid, {
        recipientName,
        address,
        city,
        postCode,
        country,
        vat,
        isVatUe,
      });
  }, [
    recipientName,
    address,
    city,
    postCode,
    country,
    vat,
    isVatUe,
    onChange,
    isFormValid,
  ]);

  return (
    <Box>
      <form ref={formEl} onSubmit={handleSubmit}>
        {[nameField, addressField].map(({ name, ...otherInputProps }) => (
          <FsFormFullWidthContainer key={name}>
            <FsSimpleInput
              {...{ ...otherInputProps, name }}
              fsVariant="outlined"
              fullWidth
            />
          </FsFormFullWidthContainer>
        ))}
        <FsFormGridWrapper isBlock={!isUpSm}>
          {[cityField, postCodeField].map(
            ({ name, ...otherInputProps }, idx) => (
              <FsFormGridItem key={name} flexGrow={idx === 0 ? 2 : 1}>
                <FsSimpleInput
                  {...{ ...otherInputProps, name }}
                  fsVariant="outlined"
                  fullWidth
                />
              </FsFormGridItem>
            )
          )}
        </FsFormGridWrapper>
        <FsFormFullWidthContainer>
          <Autocomplete
            defaultValue={defaultCountryFieldValue}
            autoHighlight
            options={countryField?.options || []}
            onChange={(_, newValue) => {
              setCountry(newValue?.value || '');
            }}
            getOptionSelected={(option, value) => option.value === value.value}
            renderOption={(option) => (
              <>
                <ReactCountryFlag
                  countryCode={option.label}
                  svg
                  style={{
                    width: 40,
                  }}
                />
                {option.value}
              </>
            )}
            getOptionLabel={({ value }) => value}
            renderInput={(params) => (
              <FsSimpleInput
                {...{
                  ...params,
                  label: countryField?.label,
                  name: countryField?.name,
                }}
                fsVariant="outlined"
                required
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'password', // disable autocomplete and autofill
                }}
              />
            )}
          />
        </FsFormFullWidthContainer>
        <Box marginBottom={0.3}>
          <FsSimpleInput {...vatField} fsVariant="outlined" fullWidth />
        </Box>
        <FsFormFullWidthContainer>
          <FsCheckboxWithLabel
            label={vatUeField.label}
            name={vatUeField.name}
            checked={vatUeField.checked as boolean}
            onChange={vatUeField.onChange}
          />
        </FsFormFullWidthContainer>
        {showButtons && (
          <Box display="flex" flexDirection="row-reverse">
            <FsPendingButton
              type="submit"
              variant="contained"
              color="primary"
              pending={pending}
              onClick={() => undefined}
              width={160}
              disabled={!isFormValid}
            >
              {defaultLocales.change}
            </FsPendingButton>
            <FsButton
              color="inherit"
              onClick={onClose}
              style={{ marginLeft: 10 }}
            >
              {defaultLocales.close}
            </FsButton>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default FsBillingInfoForm;
